const IV_LENGTH = 12;

function bytesToBase64({ bytes }: { bytes: Uint8Array }): string {
  return btoa(String.fromCharCode(...bytes));
}

function base64ToUint8Array({ value }: { value: string }): Uint8Array {
  return Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
}

export async function generateEcdhKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, [
    "deriveKey",
  ]);
}

export async function exportPublicKey({ key }: { key: CryptoKey }): Promise<string> {
  const raw = await crypto.subtle.exportKey("raw", key);
  return bytesToBase64({ bytes: new Uint8Array(raw) });
}

export async function importPublicKey({
  publicKey,
}: {
  publicKey: string;
}): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    base64ToUint8Array({ value: publicKey }),
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
}

async function deriveAesKey({
  privateKey,
  peerPublicKey,
  usage,
}: {
  privateKey: CryptoKey;
  peerPublicKey: CryptoKey;
  usage: "encrypt" | "decrypt";
}): Promise<CryptoKey> {
  return crypto.subtle.deriveKey(
    { name: "ECDH", public: peerPublicKey },
    privateKey,
    { name: "AES-GCM", length: 256 },
    false,
    [usage],
  );
}

export async function encryptAccessKey({
  plaintext,
  privateKey,
  peerPublicKey,
}: {
  plaintext: string;
  privateKey: CryptoKey;
  peerPublicKey: CryptoKey;
}): Promise<{ encrypted: string; iv: string }> {
  const aesKey = await deriveAesKey({
    privateKey,
    peerPublicKey,
    usage: "encrypt",
  });
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    new TextEncoder().encode(plaintext),
  );
  return {
    encrypted: bytesToBase64({ bytes: new Uint8Array(encrypted) }),
    iv: bytesToBase64({ bytes: iv }),
  };
}

export async function decryptAccessKey({
  encrypted,
  iv,
  privateKey,
  peerPublicKey,
}: {
  encrypted: string;
  iv: string;
  privateKey: CryptoKey;
  peerPublicKey: CryptoKey;
}): Promise<string> {
  const aesKey = await deriveAesKey({
    privateKey,
    peerPublicKey,
    usage: "decrypt",
  });
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: base64ToUint8Array({ value: iv }) },
    aesKey,
    base64ToUint8Array({ value: encrypted }),
  );
  return new TextDecoder().decode(decrypted);
}
